import { useEffect, useState } from "react";
import ReDrawer from "../../unity-ui-common/Components/ReDrawer";
import ReusableForm from "../../unity-ui-common/Components/ReForm";
import { Form, Result, Tag } from "antd";
import ReUpload from "../../unity-ui-common/Components/ReFormFields/ReUpload";
import ReInput from "../../unity-ui-common/Components/ReFormFields/ReInput";
import ReRichTextEditor from "../../unity-ui-common/Components/ReRichTextEditor/ReRichTextEditor";
import { Button } from "antd/lib";
import { DataParser } from "../../unity-ui-common/Classes/DataParser";
import * as Styles from "./EmailSafetyDocumentsDrawerStyles";
import ReTable from "../../unity-ui-common/Components/ReTable/ReTable";
import { safetyDocumentColumns } from "../../Routes/SafetyDocument/Constant";
import { distributions } from "../../API/Distribution";
import ReModal from "../../unity-ui-common/Components/ReModal";
import { ReNotification } from "../../unity-ui-common/Components/ReNotification";

interface IProps {
  visibility: boolean;
  setVisibility: Function;
  selectedSafetyDocument: any[];
  createNewColumns?:any[]
}

// The contact list template has changed across versions: the primary email
// column ships as "Primary Email ID" in the older sheet and as "Email ID" in the
// newer one, and some exports carry a title/blank row above the header. Match
// leniently instead of assuming an exact label on row 0. Order matters — the
// first entry found wins when a sheet carries more than one of these.
const PRIMARY_EMAIL_HEADERS = [
  "primary email id",
  "primary email",
  "email id",
  "email address",
  "email",
  "contact email",
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeHeader = (value: any) =>
  typeof value === "string" ? value.replace(/\s+/g, " ").trim().toLowerCase() : "";

const findEmailColumn = (rows: any[]) => {
  const limit = Math.min(rows.length, 10);
  for (let rowIndex = 0; rowIndex < limit; rowIndex++) {
    const row = rows[rowIndex];
    if (!Array.isArray(row)) continue;
    for (const header of PRIMARY_EMAIL_HEADERS) {
      const columnIndex = row.findIndex(
        (cell: any) => normalizeHeader(cell) === header
      );
      if (columnIndex !== -1) return { rowIndex, columnIndex };
    }
  }
  return null;
};

const collectEmails = (value: any, target: string[]) => {
  if (typeof value !== "string" && typeof value !== "number") return;
  String(value)
    .split(/[,;]/)
    .map((email: string) => email.trim())
    .filter((email: string) => EMAIL_REGEX.test(email))
    .forEach((email: string) => target.push(email.toLowerCase()));
};

const extractEmailsFromContactList = (rows: any[]): string[] => {
  if (!Array.isArray(rows) || !rows.length) return [];

  const header = findEmailColumn(rows);
  const emails: string[] = [];

  if (header) {
    rows.slice(header.rowIndex + 1).forEach((row: any) => {
      if (Array.isArray(row)) collectEmails(row[header.columnIndex], emails);
    });
  }

  // No recognised header, or the matched column was empty — pick the first
  // email-looking cell of every row so an unexpected template still works.
  if (!emails.length) {
    rows.forEach((row: any) => {
      if (!Array.isArray(row)) return;
      const cell = row.find(
        (value: any) => typeof value === "string" && EMAIL_REGEX.test(value.trim())
      );
      if (cell) collectEmails(cell, emails);
    });
  }

  return Array.from(new Set(emails));
};

export default function EmailSafetyDocumentsDrawer(props: IProps) {
  const [form] = Form.useForm();

  const { visibility, setVisibility, selectedSafetyDocument, createNewColumns } = props;
 
  const [successfulRequest, setSuccessfulRequest] = useState<boolean>(false);
  const [attachmentList, setAttachmentList] = useState<any>([]);
  const [response, setResponse] = useState<any>();
  const [submitButtonLoading, setSubmitButtonLoading] =
    useState<boolean>(false);
  const [contactListEmails, setContactListEmails] = useState<string[]>([]);
  const [selectedSafetyDocumentKeys, setSelectedSafetyDocumentKeys] =
    useState<any>([]);

  const handleFormSubmit = async (values: any) => {
    if (!attachmentList[0] || !values.subject || !values.body) {
      return;
    }

    if (!contactListEmails.length) {
      ReNotification({
        header: "Email Safety Documents",
        description:
          "Email not sent: No recipient email found in the uploaded contact list.",
        duration: 5,
        placement: "bottomLeft",
        type: "error",
      });
      return;
    }

    // Only complain about the selection when the selection really is empty —
    // this message used to be shown for every server-side failure.
    const safetyDocumentIDs = (selectedSafetyDocumentKeys || []).filter(
      (id: any) => !!id
    );
    if (!safetyDocumentIDs.length) {
      ReNotification({
        header: "Email Safety Documents",
        description: "Email not sent: Please select Safety Document",
        duration: 5,
        placement: "bottomLeft",
        type: "error",
      });
      return;
    }

    setSubmitButtonLoading(true);
    const res = await distributions.importAndSendMail(
      safetyDocumentIDs,
      attachmentList[0],
      values.subject,
      values.body,
      contactListEmails
    );
    setSubmitButtonLoading(false);
    if (res?.success) {
      setSuccessfulRequest(true);
      setResponse(res);
    } else {
      // Surface the real reason (identity conflicts, no importable contact,
      // report not found, network failure, ...) instead of a fixed message.
      const reason =
        res?.error ||
        res?.message ||
        (res ? "Something went wrong." : "The request could not reach the server.");
      ReNotification({
        header: "Email Safety Documents",
        description: `Email not sent: ${String(reason).replace(
          /^[A-Za-z]*Exception:\s*/,
          ""
        )}`,
        duration: 10,
        placement: "bottomLeft",
        type: "error",
      });
    }
  };

  const onBeforeAttachmentUpload = async (fileformData: any) => {
    setResponse(null);
    setSuccessfulRequest(false);
    const dataParseService = new DataParser();
    let contactListData: any = [];
    try {
      contactListData = await dataParseService.XLMSToJson(fileformData);
    } catch (e) {
      contactListData = [];
    }
    const emails = extractEmailsFromContactList(contactListData);
    setContactListEmails(emails);
    if (!emails.length) {
      ReNotification({
        header: "Email Safety Documents",
        description:
          "No email addresses found in the uploaded contact list. Please check the file has an 'Email ID' / 'Primary Email ID' column.",
        duration: 5,
        placement: "bottomLeft",
        type: "error",
      });
    }
    setAttachmentList([fileformData]);
  };
  const handleAttachmentFileRemove = (fileformData: any) => {
    setResponse(null);
    setSuccessfulRequest(false);
    const index = attachmentList.indexOf(fileformData);
    const newFileList = attachmentList.slice();
    newFileList.splice(index, 1);
    setAttachmentList(newFileList);
    setContactListEmails([]);
  };

  useEffect(() => {
    if (visibility === false) {
      form.resetFields();
      setAttachmentList([]);
      setResponse(null);
      setSuccessfulRequest(false);
      setContactListEmails([]);
    }
    setSelectedSafetyDocumentKeys(
      (selectedSafetyDocument || [])
        .map((ele: any) => ele?.id)
        .filter((id: any) => !!id)
    );
  }, [visibility, selectedSafetyDocument]);

  return (
    <div>
      <ReDrawer
        visibility={visibility}
        width={750}
        title="Email Safety Documents"
        onCancel={() => {
          setVisibility(false);
        }}
        extraContent={
          <>
            <Button
              type="primary"
              onClick={() => {
                form.submit();
              }}
              loading={submitButtonLoading}
            >
              Send
            </Button>
          </>
        }
      >
        {successfulRequest && (
          <ReModal
            width={600}
            title="Email Safety Documents"
            visibility={visibility}
            onCancel={() => {
              setVisibility(false);
            }}
            footer={false}
          >
            <Styles.ModalBody>
              <Result
                status="success"
                title="Email sent successfully"
                subTitle={
                  <>
                    Notifications were{" "}
                    <strong> sent to {response?.emailsSent} </strong> contacts{" "}
                  </>
                }
                extra={[
                  <Button
                    onClick={async () => {
                      setVisibility(false);
                    }}
                  >
                    Done
                  </Button>,
                ]}
              />
            </Styles.ModalBody>
          </ReModal>
        )}
        <ReusableForm
          formInstance={form}
          onSubmit={handleFormSubmit}
          initialFormValues={{
            subject: "You have received Safety Documents from UNITYdx",
          }}
        >
          <div>
            <ReUpload
              errorMessage="Please upload the file"
              accept="xlms"
              label="Contact List"
              BtnTitle="Click to Upload File"
              name="attachments"
              onBeforeUpload={onBeforeAttachmentUpload}
              onRemove={handleAttachmentFileRemove}
              fileListMaxCount={1}
              fileList={attachmentList}
              multiple
              required
            />
            <Styles.EmailTo>
              <span className="to">To</span>
              <div className="emails">
                {contactListEmails.map((email: string) => {
                  return (
                    <Tag
                      key={email}
                    >
                      {email}
                    </Tag>
                  );
                })}
              </div>
            </Styles.EmailTo>
          </div>
          <ReInput
            name="subject"
            label="Subject"
            max={300}
            type="simple"
            required
            form={form}
          />
          <ReRichTextEditor
            required
            name="body"
            label="Email Body"
            form={form}
          />
        </ReusableForm>
       
        <ReTable
          columns={createNewColumns}
          data={selectedSafetyDocument}
          name="Selected Safety Document"
          title="Selected Safety Document"
          rowSelection={{
            selectedRowKeys: selectedSafetyDocumentKeys,
            setSelectedRowKeys: setSelectedSafetyDocumentKeys,
            type: "checkbox",
            rowKey: "id",
          }}
          scroll={{
            x: 2500,
            y: 800,
          }}
        />
      </ReDrawer>
    </div>
  );
}
